import { EMuiGridStatus, GridStateHelper } from 'lib/MuiGrid/MuiGrid';
import * as React from 'react';
import { EnrichedMenuOption, MenuOptionOverrides } from '../MenuOptionsModel';
import { isArray, isDefined, isUndefined } from 'lib/typeguards';
import { useDispatch } from 'react-redux';
import {
    MESSAGE_MENU_SCHEDULING_UPDATE_SUCCESS,
    MESSAGE_MENU_SCHEDULING_UPDATE_ERROR
} from 'config/messages';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { menuOptionsApi } from '../MenuOptionsApi';

export function useMenuOptionChange(locationId: string, list: EnrichedMenuOption[]) {
    const dispatch = useDispatch();

    const gridRef = React.useRef<GridStateHelper>(null);
    const [menuOptions, setMenuOptions] = React.useState<EnrichedMenuOption[]>([]);
    const [readyToSave, setReadyToSave] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);

    React.useEffect(() => {
        setMenuOptions(getMenuOptions(list));
    }, [list]);

    const handleStatusChange = React.useCallback(
        (status: EMuiGridStatus) => {
            if (readyToSave && status === EMuiGridStatus.DEFAULT) {
                setReadyToSave(false);
            }
            if (!readyToSave && status === EMuiGridStatus.CHANGED) {
                setReadyToSave(true);
            }
        },
        [readyToSave]
    );

    const handleCancel = React.useCallback(() => {
        if (gridRef.current) {
            gridRef.current.reset();
            setMenuOptions(getMenuOptions(list));
        }
    }, [setMenuOptions, list]);

    const handleSubmit = React.useCallback(async () => {
        // Initialize update payload with existing overrides
        const updatePayload: MenuOptionOverrides[] = menuOptions.map(option => ({
            menuId: option.menuId,
            ...option.overrides
        }));

        try {
            if (!gridRef.current) {
                throw new Error('Menu Option table not defined');
            }

            const { changes } = gridRef.current.getState() ?? {};

            setIsProcessing(true);

            for (const [id, changesInRow] of Object.entries(changes)) {
                const index = updatePayload.findIndex(option => option.menuId === id);
                const previousOverride = index > -1 ? updatePayload[index] : undefined;

                const updatedOverride: MenuOptionOverrides = {
                    menuId: id
                };

                if (isArray(changesInRow?.perkIds)) {
                    updatedOverride.perkIds = changesInRow.perkIds;
                } else if (isUndefined(changesInRow?.perkIds) && previousOverride?.perkIds) {
                    updatedOverride.perkIds = previousOverride?.perkIds;
                }

                const parsedPriorityOrder = parseInt(changesInRow?.priorityOrder);
                if (parsedPriorityOrder) {
                    updatedOverride.priorityOrder = parsedPriorityOrder;
                } else if (isUndefined(changesInRow?.priorityOrder) && previousOverride?.priorityOrder) {
                    updatedOverride.priorityOrder = previousOverride.priorityOrder;
                }

                updatePayload[index] = updatedOverride;
            }

            const result = await menuOptionsApi.updateMenuOptionOverrides(locationId, updatePayload);

            if (result && !result.ok) {
                throw new Error(result.body.message);
            }

            const mergedOptions = menuOptions.map(option => ({
                ...option,
                overrides: updatePayload.find(payload => payload.menuId === option.menuId)
            }));

            const updatedMenuOptions = getMenuOptions(mergedOptions);

            setMenuOptions(updatedMenuOptions);
            setIsProcessing(false);

            gridRef.current.reset();

            dispatch(enqueueSnackbar(MESSAGE_MENU_SCHEDULING_UPDATE_SUCCESS, { variant: 'success' }));
        } catch (err) {
            console.error(err);
            dispatch(enqueueSnackbar(MESSAGE_MENU_SCHEDULING_UPDATE_ERROR, { variant: 'error' }));
            setIsProcessing(false);
        }
    }, [gridRef, locationId, menuOptions, dispatch]);

    return {
        gridRef,
        readyToSave,
        menuOptions,
        isProcessing,
        handleSubmit,
        handleStatusChange,
        handleCancel
    };
}

// Adds overridden fields to the menu options
function getMenuOptions(menuOptions: EnrichedMenuOption[]): EnrichedMenuOption[] {
    const collapsedOptions: EnrichedMenuOption[] = [];

    for (const option of menuOptions) {
        const modifiedOption = option;
        const overrides = option.overrides;

        if (overrides) {
            if (overrides.perkIds && overrides.perkIds.length > 0) {
                modifiedOption._overriden = addToSet(modifiedOption._overriden, 'perkIds');
            } else {
                modifiedOption._overriden = removeFromSet(modifiedOption._overriden, 'perkIds');
            }

            if (isDefined(overrides.priorityOrder)) {
                modifiedOption._overriden = addToSet(modifiedOption._overriden, 'priorityOrder');
            } else {
                modifiedOption._overriden = removeFromSet(modifiedOption._overriden, 'priorityOrder');
            }
        }

        collapsedOptions.push(modifiedOption);
    }

    return collapsedOptions;
}

function addToSet(set: Set<string> | undefined, item: string): Set<string> {
    if (!set) {
        return new Set<string>([item]);
    }
    set.add(item);
    return set;
}

function removeFromSet(set: Set<string> | undefined, item: string): Set<string> {
    if (!set) {
        return new Set<string>();
    }
    set.delete(item);
    return set;
}
